import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import { CASE_POSTS, PRODUCT_PAGE } from '@/data/mockData';
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  환경 — 환경 IoT 솔루션 (랜딩)  (/environment)
//  [2026-10-12 사용자] '화이트 airy' 리뉴얼 2호 — 안전 대표페이지(/safety)와 동일 디자인 철학으로 통일.
//    목표 디자인 evs.com/company/brand-resources(섹션·여백·배경교차) + corporate-events(아이콘 크기).
//    ★섹션 역할별 '다른 표현'으로 단조로움 제거(= /safety 승인 패턴 그대로):
//      텍스트 인트로 / 선+원 flow 타임라인 / 제품 이미지카드 / feat(borderless) 카드 / 케이스 카드 /
//      ★핵심 섹션(시장·보조금)=풀컬러 fill 카드 / CTA 틴트밴드. 흰 ↔ 연회색(alt) 교차.
//    ★히어로는 리뉴얼 대상 아님 = 다크 이미지 그대로 유지(모듈만 w로 통일, eyebrow 제거해 /safety와 동일 구조).
//    섹션/콘텐츠/링크는 기존(기획서 v7 4-1) 유지, 테마만 제품다크(s/t) → 화이트(w)로 교체. 비파괴.
//  기존 구조(4-1): ①솔루션개요 ②전체구성 ③제품 ④EVS강점 ⑤KC·승인 ⑥적용시설 ⑦설치사례 ⑦-b시장·보조금 ⑧행동.
//  연결(문서): ①GreenLink ②시스템 ③제품상세 ④설치사업 ⑤인증 ⑥시설안내 ⑦환경사례 ⑦-b상담 ⑧문의.
//  실데이터: ③제품(EVS-G200·계측기) ⑦설치사례. ⑦-b 수치 근거=[[evs-brochure-251128-data]](브로셔 pg7).
// ============================================================================

const HERO_IMG = '/images/hero/hero-guide.jpg'; // [2026-10-11 사용자] 환경 메뉴 히어로 이미지 통일 = /guide 기준(유지)
const SLOGAN = '대기배출시설의 가동정보를 실시간 측정하고, 방지시설 계측기와 IoT 게이트웨이로 수집해 환경부 GreenLink로 안정적으로 전송합니다.';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

export default function EnvironmentLanding() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  const envCases = CASE_POSTS.filter((p) => p.domain === 'environment' && p.category === '설치').slice(0, 3);
  const gw = PRODUCT_PAGE.gateway;

  return (
    <main className={w.page}>
      <PageLoader />

      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '환경', '환경 IoT 솔루션']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>환경 IoT 솔루션</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 솔루션 개요 → GreenLink (텍스트 인트로) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>OVERVIEW</p>
            <h2 className={w.h2}>배출·방지시설 데이터를 실시간 수집·전송</h2>
            <p className={w.lead}>소규모 대기배출시설의 가동 데이터를 계측기가 측정하고, 게이트웨이가 수집해 환경부 GreenLink로 자동 전송합니다. 법적 원격 모니터링 의무에 대응합니다.</p>
            <a className={w.moreLink} onClick={() => nav('/environment/greenlink')}>GreenLink 전송 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ② 전체 구성 → 시스템 — [2026-10-23 사용자] 데이터 여정 카드(이미지45 재해석, 환경=그린 변주). 전폭 다크 네이비 밴드 + 그린 그라데이션 카드 5장 */}
      <section className={`${w.section} ${w.jband} ${w.jEnv}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>SYSTEM</p>
            <h2 className={w.h2}>시설에서 GreenLink까지 하나로 연결</h2>
            <div className={`${w.jgrid} ${w.jgrid5}`}>
              {[
                { n: '01', t: '시설', i: 'factory', d: '배출·방지시설의 가동 상태', c: 'linear-gradient(160deg,#0C3B2C,#07281E)' },
                { n: '02', t: '계측기', i: 'gauge', d: '전류·차압·온도·pH 측정', c: 'linear-gradient(160deg,#0F5038,#0A3A28)' },
                { n: '03', t: 'EVS-G200', i: 'gateway', d: '수집·표시·저장', c: 'linear-gradient(160deg,#126A48,#0D4E34)' },
                { n: '04', t: 'VPN', i: 'signal', d: '보안 전송', c: 'linear-gradient(160deg,#158757,#107040)' },
                { n: '05', t: 'GreenLink', i: 'monitor', d: '환경부 자동 전송', c: 'linear-gradient(160deg,#1BA868,#13884F)' },
              ].map((x) => (
                <div key={x.n} className={w.jcard} style={{ ['--jc' as string]: x.c } as React.CSSProperties}>
                  <span className={w.jcardNum}>{x.n}</span>
                  <div className={w.jcardTitle}>{x.t}</div>
                  <div className={w.jcardLine} />
                  <div className={w.jcardDesc}>{x.d}</div>
                  <div className={w.jcardIco}>{GUIDE_ICON[x.i]}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ③ 제품 → 제품 상세 (제품 이미지 링크카드) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>PRODUCTS</p>
            <h2 className={w.h2}>게이트웨이와 계측기</h2>
            <div className={w.prodGrid}>
              <a className={`${w.prodCard} ${w.cBlue}`} onClick={() => nav('/products/evs-g200')}>
                <div className={w.prodImg}><img src={gw.image} alt="EVS-G200" loading="lazy" /></div>
                <div className={w.prodBody}><strong>IoT Gateway {gw.name}</strong><em>계측 데이터를 수집·전송하는 IoT 게이트웨이</em></div>
                <span className={w.prodArrow}>→</span>
              </a>
              <a className={`${w.prodCard} ${w.cTeal}`} onClick={() => nav('/products/instruments')}>
                <div className={w.prodImg}><img src="/images/products/product-2-f.jpg" alt="환경 계측기 4종" loading="lazy" /></div>
                <div className={w.prodBody}><strong>환경 계측기 4종</strong><em>전류·차압·온도·pH계</em></div>
                <span className={w.prodArrow}>→</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ④ EVS 강점 → 설치사업 (feat borderless 카드, 5개) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>WHY EVS</p>
            <h2 className={w.h2}>제조부터 A/S까지 일괄 지원</h2>
            <div className={`${w.grid} ${w.grid5}`}>
              {[
                { l: '자체 제조', i: 'gateway', d: '게이트웨이를 직접 설계·생산', c: w.cBlue },
                { l: '현장 실사', i: 'building', d: '시설별 현장 조사·설계', c: w.cTeal },
                { l: '설치', i: 'solution', d: '계측기·게이트웨이 설치', c: w.cIndigo },
                { l: '연동', i: 'signal', d: 'GreenLink 전송 연동', c: w.cAmber },
                { l: 'A/S', i: 'action', d: '설치 후 유지보수·대응', c: w.cCoral },
              ].map((x) => (
                <div key={x.l} className={`${w.card} ${x.c} ${w.feat}`}>
                  <Ic n={x.i} /><h3>{x.l}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/business/install')}>설치사업 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑤ KC·승인 → 인증 페이지 (텍스트 인트로) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>CERTIFICATION</p>
            <h2 className={w.h2}>KC · 환경공단 승인</h2>
            <p className={w.lead}>게이트웨이(EVS-G200) 단수형·복수형·교대형 전 모델이 KC 인증을 완료했으며, 환경공단 관련 승인·확인을 바탕으로 GreenLink에 전송합니다.</p>
            <a className={w.moreLink} onClick={() => nav('/company/certifications')}>인증·기술 페이지 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑥ 적용시설 → 시설 안내 (텍스트 인트로) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>FACILITIES</p>
            <h2 className={w.h2}>시설별 적용 계측기</h2>
            <p className={w.lead}>집진·여과·흡수·세정 등 방지시설 종류에 따라 필요한 계측기와 설치 위치가 달라집니다. 시설별 적용 예시를 안내합니다.</p>
            <a className={w.moreLink} onClick={() => nav('/guide')}>배출·방지시설 안내 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑦ 설치사례 → 환경 사례 (케이스 카드) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>CASES</p>
            <h2 className={w.h2}>대표 설치사례</h2>
            <div className={w.caseGrid}>
              {envCases.map((c) => (
                <a key={c.slug} className={w.caseCard} onClick={() => nav(`/cases/${c.slug}`)}>
                  <div className={w.caseImg}><img src={c.cover} alt={c.title} loading="lazy" /></div>
                  <strong>{c.title}</strong><em>{c.date}</em>
                </a>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/cases?tab=environment')}>환경 사례 전체 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑦-b 시장규모·보조금 → 도입 유인 (★핵심 섹션 = 풀컬러 fill 카드) */}
      {/* [2026-10-11 사용자] 공식 브로셔(251128 pg7) '시장규모·보조금'. 그래프 미사용·수치만 재구성. 근거: [[evs-brochure-251128-data]]. */}
      {/* [2026-10-12] 화이트 리뉴얼 — 도입 유인=실무적 핵심 → /safety '안전분야'처럼 풀컬러 fill 카드로 강조. 60% 지원을 coral로 포인트. */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>MARKET &amp; SUBSIDY</p>
            <h2 className={w.h2}>지금이 IoT 측정기기 도입 적기입니다</h2>
            <p className={w.lead}>소규모 대기배출 사업장의 IoT 측정기기 부착이 꾸준히 확대되고 있으며, 2026년에는 도입 비용의 상당 부분을 국가·지자체 보조금으로 지원받을 수 있습니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cBlue} ${w.fill}`}><Ic n="factory" /><h3>56,306개소</h3><p>2024년 전체 4·5종 대기배출 사업장 규모입니다. (출처: 환경부 환경통계연감)</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.fill}`}><Ic n="gateway" /><h3>33,695개소 부착</h3><p>IoT 측정기기 부착 사업장(부착 배출구 80,523개)으로 부착이 계속 확대되고 있습니다. (’25.10.20, 한국환경공단)</p></div>
              <div className={`${w.card} ${w.cCoral} ${w.fill}`}><Ic n="won" /><h3>최대 60% 지원</h3><p>2026년 도입 보조금 — 국비 40% + 지방비 20% 지원, 사업장 부담 40%. (출처: 환경부 2026년 지자체 보조사업)</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/support/consult')}>보조금·도입 상담 →</a>
          </Reveal>
        </div>
      </section>

      {/* ⑧ 행동 → 문의 (CTA 틴트밴드) */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>도입을 검토 중이신가요?</h2>
            <p className={w.lead}>현장실사·견적으로 바로 연결됩니다. 카탈로그는 준비되는 대로 제공됩니다.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav('/support/consult')}>현장실사 상담</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/inquiry?from=/environment')}>견적 문의</a>
              <span className={`${w.ctaGhost} ${w.ctaSoon}`} aria-disabled>카탈로그 (준비중)</span>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
